import React, { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../AxiosInstance";
import SidebarLayout from "../SidebarLayout";
import CourseSearchForm from "./CourseSearchForm";
import CourseTable from "./CourseTable";
import CoursePagination from "./CoursePagination";
import { Container, Typography, Box } from "@mui/material";
import { useNotification } from "../NotificationContext";
import CourseDialog from "./CourseDialog";
import { jwtDecode } from "jwt-decode";

const COURSE_CACHE_KEY = "coursemain_state_v1";

const readCourseCache = () => {
  try {
    const raw = sessionStorage.getItem(COURSE_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const CourseMain = () => {
  const cached = readCourseCache();
  const [courses, setCourses] = useState([]);
  const [currentPage, setCurrentPage] = useState(cached.currentPage || 1);
  const [itemsPerPage] = useState(10);
  const [currentCourse, setCurrentCourse] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [processed, setProcessed] = useState(cached.processed ?? "");
  const [active, setActive] = useState(cached.active ?? "");
  const { showNotification } = useNotification();
  const [icpisManager, setIcpisManager] = useState(cached.icpisManager ?? "");
  const [venueId, setVenueId] = useState(cached.venueId ?? "");
  const [adAddress, setAdAddress] = useState(cached.adAddress ?? "");
  const [fromDate, setFromDate] = useState(cached.fromDate ?? null);
  const [toDate, setToDate] = useState(cached.toDate ?? null);
  const [totalPages, setTotalPages] = useState(cached.totalPages || 1); // 添加 totalPages 状态
  const [totalCourses, setTotalCourses] = useState(cached.totalCourses || 0);
  const [isChecked, setIsChecked] = useState(false);
  const [isEnrollwareChecked, setIsEnrollwareChecked] = useState(false);
  const [courseStatus, setCourseStatus] = useState(cached.courseStatus ?? "");
  const handleSearchRef = useRef(null);
  useEffect(() => {
    if (Array.isArray(cached.courses) && cached.courses.length > 0) {
      setCourses(cached.courses);
    }
  }, []);

  const [icpisManagers, setIcpisManagers] = useState([]);
  const token = localStorage.getItem("token");
  let userRole = null;
  let icpisname = null;
  if (token) {
    try {
      const decodedToken = jwtDecode(token);
      userRole = decodedToken.claims?.role || null;
      icpisname = decodedToken.claims?.icpiename || decodedToken.claims?.firstname || null;
    } catch (e) {
      userRole = null;
      icpisname = null;
    }
  }

  const fetchIcpisManagers = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/icpie/allicpiamanger");
      setIcpisManagers(response.data);
    } catch (error) {
      console.error("Error fetching icpis managers:", error);
    }
  }, []);

  const courseDateTimeTs = (c) => {
    const d = String(c?.date || "").slice(0, 10);
    const tRaw = c?.startTime || c?.start_time || "";
    const t = String(tRaw).trim();
    const hhmmss =
      t.length >= 8
        ? t.slice(0, 8)
        : t.length >= 5
          ? `${t.slice(0, 5)}:00`
          : "00:00:00";
    if (!d) return 0;
    const ts = Date.parse(`${d}T${hhmmss}`);
    return Number.isFinite(ts) ? ts : 0;
  };

  const handleSearch = useCallback(
    (forcePage = null, overrides = null) => {
      if (isLoading) return;
      setIsLoading(true);
      const pageToFetch = forcePage !== null ? forcePage : currentPage;
      if (forcePage !== null) {
        setCurrentPage(forcePage);
      }

      const isReusableAds = Boolean(overrides?.reusableAds);
      const effectiveCourseStatus = isReusableAds
        ? "1"
        : overrides?.courseStatus !== undefined
          ? overrides.courseStatus
          : courseStatus;
      const effectiveFromDate = isReusableAds
        ? overrides.fromDate
        : overrides?.fromDate !== undefined
          ? overrides.fromDate
          : fromDate;
      const effectiveToDate = isReusableAds
        ? null
        : overrides?.toDate !== undefined
          ? overrides.toDate
          : toDate;
      const cutoffTs = isReusableAds ? overrides.cutoffTs : null;

      // 对于 ROLE_ICPIS，强制使用自己 icpiename 作为 icpisManager 过滤条件
      const icpisManagerParam =
        userRole === "ROLE_ICPIS" && icpisname ? icpisname : icpisManager;

      let params = {
        pageNum: pageToFetch,
        pageSize: isReusableAds ? 500 : itemsPerPage,
        icpisManager: icpisManagerParam,
        date: "",
        isActive: active,
        isProcessed: processed,
        fromDate: effectiveFromDate,
        toDate: effectiveToDate,
        venueId: venueId,
        address: adAddress,
        courseStatus: effectiveCourseStatus,
      };

      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });
      const token = localStorage.getItem("token");

      const authHeader = "Bearer " + token;

      axiosInstance
        .get("/course", {
          params: params, // 将 params 正确放入第二个参数中的 params 字段
          headers: {
            Authorization: authHeader, // 传递 Bearer token
          },
        })
        .then((response) => {
          if (response.data.message === "success") {
            const items = response.data.data ? response.data.data.items : [];
            let rows = Array.isArray(items) ? [...items] : [];
            if (isReusableAds && cutoffTs != null) {
              rows = rows.filter((c) => courseDateTimeTs(c) >= cutoffTs);
              rows.sort((a, b) => courseDateTimeTs(a) - courseDateTimeTs(b)); // asc: nearest → furthest
              setCourses(rows);
              // Full sorted list in one view so soonest reusable ads are on top
              setTotalPages(1);
              setTotalCourses(rows.length);
            } else {
              const sorted = rows.sort(
                (a, b) => courseDateTimeTs(b) - courseDateTimeTs(a), // desc by course date + start time
              );
              setCourses(sorted);
              setTotalPages(
                Math.ceil(response.data.data.totalElement / itemsPerPage),
              );
              setTotalCourses(response.data.data.totalElement);
            }
            if (
              response.data.warnings &&
              Object.keys(response.data.warnings).length > 0
            ) {
              Object.entries(response.data.warnings).forEach(([key, value]) => {
                showNotification(value, "warning");
              });
            }
          } else {
            // 失败时处理警告
            showNotification("Course search failed!", "error");

            if (
              response.data.warnings &&
              Object.keys(response.data.warnings).length > 0
            ) {
              Object.entries(response.data.warnings).forEach(([key, value]) => {
                showNotification(value, "warning");
              });
            }
          }
        })
        .catch((error) => {
          console.error("Error in request:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [
      active,
      adAddress,
      courseStatus,
      currentPage,
      icpisManager,
      icpisname,
      isLoading,
      itemsPerPage,
      fromDate,
      processed,
      showNotification,
      toDate,
      userRole,
      venueId,
    ],
  );

  const handleReusableAds = useCallback(() => {
    const cutoff = new Date(Date.now() + 48 * 60 * 60 * 1000);
    const yyyy = cutoff.getFullYear();
    const mm = String(cutoff.getMonth() + 1).padStart(2, "0");
    const dd = String(cutoff.getDate()).padStart(2, "0");
    const fromDateStr = `${yyyy}-${mm}-${dd}`;

    setCourseStatus("1");
    setFromDate(fromDateStr);
    setToDate(null);
    setCurrentPage(1);

    handleSearch(1, {
      reusableAds: true,
      courseStatus: "1",
      fromDate: fromDateStr,
      toDate: null,
      cutoffTs: cutoff.getTime(),
    });
  }, [handleSearch]);

  useEffect(() => {
    handleSearchRef.current = handleSearch;
  }, [handleSearch]);

  useEffect(() => {
    // Only trigger auto-search on initial load / page change.
    if (handleSearchRef.current) {
      handleSearchRef.current();
    }
  }, [currentPage]);

  useEffect(() => {
    // 只有 ROLE_ICPIE 需要选择不同 ICPIS Manager 才拉取下拉列表
    if (userRole === "ROLE_ICPIE") {
      fetchIcpisManagers();
    }
  }, [fetchIcpisManagers, userRole]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        COURSE_CACHE_KEY,
        JSON.stringify({
          currentPage,
          processed,
          active,
          icpisManager,
          venueId,
          adAddress,
          fromDate,
          toDate,
          totalPages,
          totalCourses,
          courseStatus,
          courses,
        }),
      );
    } catch {
      // ignore cache write errors
    }
  }, [
    currentPage,
    processed,
    active,
    icpisManager,
    venueId,
    adAddress,
    fromDate,
    toDate,
    totalPages,
    totalCourses,
    courseStatus,
    courses,
  ]);

  //   const fetchVenues = async () => {
  //     try {
  //       const response = await axiosInstance.get('/venue');
  //       if (response.data.message === "success") {
  //         setVenues(response.data.data.items);
  //       } else {
  //         showNotification('Failed to fetch venues!', 'error');
  //       }
  //     } catch (error) {
  //       console.error('Error fetching venues:', error);
  //     }
  //   };

  // const fetchSchedules = async () => {
  //   try {
  //     const response = await axiosInstance.get('/schedule');
  //     if (response.data.message === "success") {
  //       setSchedules(response.data.data.items);
  //     } else {
  //       showNotification('Failed to fetch schedules!', 'error');
  //     }
  //   } catch (error) {
  //     console.error('Error fetching schedules:', error);
  //   }
  // };

  const handleClickOpen = (course) => {
    setCurrentCourse(course);
    setOpen(true);
    setIsEditMode(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentCourse((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    console.log("Saving course with data:", currentCourse);
    axiosInstance
      .put("/course", currentCourse, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setCourses((prev) => {
          const updatedCourses = prev.map((c) => {
            if (c.id === currentCourse.id) {
              console.log(`Updating course with id: ${currentCourse.id}`);
              return currentCourse;
            }
            return c;
          });

          // Check for duplicate keys
          const courseIds = updatedCourses.map((c) => c.id);
          const duplicateIds = courseIds.filter(
            (id, index) => courseIds.indexOf(id) !== index,
          );
          if (duplicateIds.length > 0) {
            console.warn("Duplicate course IDs found:", duplicateIds);
          }

          return updatedCourses;
        });
        showNotification("Course updated successfully!", "success");
        setOpen(false);
        handleSearch(); // Check after save
      })
      .catch((error) => {
        console.error("Error in request:", error);
        showNotification("Course update failed!", "error");
      });
  };

  const handleInsert = () => {
    axiosInstance
      .post("/course", currentCourse, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        showNotification("Course added successfully!", "success");
      })
      .catch((error) => {
        console.error("Error in request:", error);
      });
  };

  const handleDelete = () => {
    axiosInstance
      .delete("/course", { params: { courseid: currentCourse.courseid } })
      .then((response) => {
        showNotification("Course deleted!", "success");
      })
      .catch((error) => {
        console.error("Error in request", error);
      });
  };
  //关闭
  const handleClose = () => {
    setOpen(false);
  };
  const handleReset = () => {
    setCurrentPage(1);
    setFromDate(null);
    setToDate(null);
    setAdAddress("");
    setIcpisManager("");
    setVenueId("");
    setActive("");
    setProcessed("");
    setCourseStatus("");
    showNotification("Reset successfully!", "success");
    // 重置后自动加载所有数据
    setTimeout(() => {
      handleSearch(1);
    }, 100);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  return (
    <SidebarLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <CourseSearchForm
          handleSearch={handleSearch}
          handleReset={handleReset}
          handleReusableAds={handleReusableAds}
          active={active}
          processed={processed}
          setActive={setActive}
          setProcessed={setProcessed}
          setIcpisManager={setIcpisManager}
          icpisManager={icpisManager}
          icpisManagers={icpisManagers}
          venueId={venueId}
          setVenueId={setVenueId}
          adAddress={adAddress}
          setAdAddress={setAdAddress}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          courseStatus={courseStatus}
          setCourseStatus={setCourseStatus}
          userRole={userRole}
        />
        <CourseTable currentItems={courses} handleClickOpen={handleClickOpen} />
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="nowrap" gap={2}>
          {/* 左侧的分页 */}
          <CoursePagination
            totalPages={totalPages}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
          />

          {/* 右侧的额外数据容器 */}
          <Container
            sx={{ ml: "auto", p: 1, border: "1px solid grey", borderRadius: "2px", flexShrink: 0 }}
          >
            <Typography variant="h6">AD Details</Typography>
            <Typography>
              The amount of AD:{totalCourses ? totalCourses : 0}{" "}
            </Typography>
            <Typography>Unit Price: $55</Typography>
            <Typography>
              Totoal Price:${totalCourses ? totalCourses * 55 : 0}{" "}
            </Typography>
          </Container>
        </Box>
      </Container>
      <CourseDialog
        open={open}
        handleClose={handleClose}
        isEditMode={isEditMode}
        currentCourse={currentCourse}
        handleChange={handleChange}
        handleSave={handleSave}
        handleInsert={handleInsert}
        handleDelete={handleDelete}
        isChecked={isChecked}
        setIsChecked={setIsChecked}
        isEnrollwareChecked={isEnrollwareChecked}
        setIsEnrollwareChecked={setIsEnrollwareChecked}
        courseStatus={courseStatus}
        setCourseStatus={setCourseStatus}
      />
    </SidebarLayout>
  );
};

export default CourseMain;
